import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";

/**
 * Background photo for the homepage full-screen stages: drifts slower than the
 * page as the section passes through the viewport. Falls back to a static image
 * during SSR and when reduced motion is preferred.
 */
export function ParallaxImage({
  src,
  className = "",
  strength = 12,
  eager = false,
}: {
  src: string;
  className?: string;
  /** Vertical drift as a percentage of the image height. */
  strength?: number;
  eager?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [`-${strength}%`, `${strength}%`]);

  const scale = 1 + strength / 50;

  const isMedia = src.startsWith("/media/");
  const base = src.slice(0, src.lastIndexOf(".") >= 0 ? src.lastIndexOf(".") : undefined);
  const set = (ext: string) => [640, 1024, 1600].map((w) => `${base}-${w}.${ext} ${w}w`).join(", ");

  return (
    <div ref={ref} className={`absolute inset-0 overflow-hidden ${className}`} aria-hidden>
      <picture>
        {isMedia ? (
          <>
            <source type="image/avif" srcSet={set("avif")} sizes="100vw" />
            <source type="image/webp" srcSet={set("webp")} sizes="100vw" />
          </>
        ) : null}
        <motion.img
          src={src}
          alt=""
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={eager ? "high" : "low"}
          style={reduced ? {} : { y, scale }}
          className="h-full w-full object-cover"
        />
      </picture>
    </div>
  );
}

